// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Event {
  id: string;
  name: string;
}

interface Section {
  id?: string;
  name: string;
  display_order: number;
  sort_field_id?: string;
  sort_direction?: string;
  fields: Field[];
}

interface Field {
  id?: string;
  field_name: string;
  field_type: string;
  unit: string;
  display_order: number;
}

const FIELD_TYPES = [
  { value: 'number', label: 'Number' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'time', label: 'Time' },
  { value: 'text', label: 'Text' }
];

const LeaderboardBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [leaderboardData, setLeaderboardData] = useState({
    name: '',
    description: '',
    event_id: ''
  });
  const [sections, setSections] = useState<Section[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
    if (editId) {
      fetchLeaderboardData();
    }
  }, [editId]);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to fetch events');
    }
  };

  const fetchLeaderboardData = async () => {
    if (!editId) return;

    try {
      setIsLoading(true);
      
      // Fetch leaderboard basic data
      const { data: leaderboard, error: leaderboardError } = await supabase
        .from('leaderboards')
        .select('*')
        .eq('id', editId)
        .single();

      if (leaderboardError) throw leaderboardError;

      setLeaderboardData({
        name: leaderboard.name,
        description: leaderboard.description || '',
        event_id: leaderboard.event_id || ''
      });

      // Fetch sections and fields
      const { data: sectionsData, error: sectionsError } = await supabase
        .from('leaderboard_sections')
        .select(`
          *,
          leaderboard_fields!section_id(*)
        `)
        .eq('leaderboard_id', editId)
        .order('display_order');

      if (sectionsError) throw sectionsError;

      const formattedSections = sectionsData.map(section => ({
        id: section.id,
        name: section.name,
        display_order: section.display_order,
        sort_field_id: section.sort_field_id || '',
        sort_direction: section.sort_direction || 'desc',
        fields: section.leaderboard_fields.map((field: any) => ({
          id: field.id,
          field_name: field.field_name,
          field_type: field.field_type,
          unit: field.unit || '',
          display_order: field.display_order
        })).sort((a: Field, b: Field) => a.display_order - b.display_order)
      }));

      setSections(formattedSections);
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
      toast.error('Failed to fetch leaderboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const addSection = () => {
    const newSection: Section = {
      name: '',
      display_order: sections.length,
      sort_field_id: '',
      sort_direction: 'desc',
      fields: []
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (index: number, field: keyof Section, value: any) => {
    const updatedSections = [...sections];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    setSections(updatedSections);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < sections.length) {
      [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
      newSections.forEach((section, i) => {
        section.display_order = i;
      });
      setSections(newSections);
    }
  };

  const addField = (sectionIndex: number) => {
    const updatedSections = [...sections];
    const newField: Field = {
      field_name: '',
      field_type: 'number',
      unit: '',
      display_order: updatedSections[sectionIndex].fields.length
    };
    updatedSections[sectionIndex].fields.push(newField);
    setSections(updatedSections);
  };

  const updateField = (sectionIndex: number, fieldIndex: number, field: keyof Field, value: any) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].fields[fieldIndex] = {
      ...updatedSections[sectionIndex].fields[fieldIndex],
      [field]: value
    };
    setSections(updatedSections);
  };

  const removeField = (sectionIndex: number, fieldIndex: number) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].fields = updatedSections[sectionIndex].fields.filter((_, i) => i !== fieldIndex);
    setSections(updatedSections);
  };

  const saveLeaderboard = async () => {
    try {
      setIsLoading(true);

      let leaderboardId = editId;

      // Save or update leaderboard
      if (editId) {
        const { error } = await supabase
          .from('leaderboards')
          .update(leaderboardData)
          .eq('id', editId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('leaderboards')
          .insert({
            name: leaderboardData.name,
            description: leaderboardData.description || null,
            event_id: leaderboardData.event_id || null,
            is_published: false
          })
          .select()
          .single();

        if (error) throw error;
        leaderboardId = data.id;
      }

      // Delete existing sections and fields if editing
      if (editId) {
        await supabase.from('leaderboard_sections').delete().eq('leaderboard_id', editId);
      }

      // Save sections and fields
      for (const section of sections) {
        const { data: sectionData, error: sectionError } = await supabase
          .from('leaderboard_sections')
          .insert([{
            leaderboard_id: leaderboardId,
            name: section.name,
            display_order: section.display_order,
            sort_field_id: null, // Will be updated after fields are created
            sort_direction: section.sort_direction || 'desc'
          }])
          .select()
          .single();

        if (sectionError) throw sectionError;

        // Save fields for this section
        if (section.fields.length > 0) {
          const fieldsToInsert = section.fields.map(field => ({
            section_id: sectionData.id,
            field_name: field.field_name,
            field_type: field.field_type,
            unit: field.unit,
            display_order: field.display_order
          }));

          const { data: fieldsData, error: fieldsError } = await supabase
            .from('leaderboard_fields')
            .insert(fieldsToInsert)
            .select();

          if (fieldsError) throw fieldsError;

          // Update section with sort field if specified
          if (section.sort_field_id) {
            // Find the field ID based on the field name or use the ID directly
            let sortFieldId = section.sort_field_id;
            
            // If sort_field_id is a field name, find the corresponding ID
            if (fieldsData) {
              const sortField = fieldsData.find(field => 
                field.field_name === section.sort_field_id || field.id === section.sort_field_id
              );
              
              if (sortField) {
                sortFieldId = sortField.id;
              }
            }
            
            await supabase
              .from('leaderboard_sections')
              .update({ 
                sort_field_id: sortFieldId,
                sort_direction: section.sort_direction || 'desc'
              })
              .eq('id', sectionData.id);
          }
        }
      }

      toast.success(editId ? 'Leaderboard updated successfully' : 'Leaderboard created successfully');
      navigate('/admin-dashboard/leaderboards');
    } catch (error) {
      console.error('Error saving leaderboard:', error);
      toast.error('Failed to save leaderboard');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Step 1: Basic Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="name">Leaderboard Name</Label>
          <Input
            id="name"
            value={leaderboardData.name}
            onChange={(e) => setLeaderboardData({ ...leaderboardData, name: e.target.value })}
            placeholder="e.g., Nancy's Hoop Circuit"
          />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={leaderboardData.description}
            onChange={(e) => setLeaderboardData({ ...leaderboardData, description: e.target.value })}
            placeholder="Optional description of the leaderboard"
          />
        </div>
        <div>
          <Label htmlFor="event">Associated Event</Label>
          <Select
            value={leaderboardData.event_id}
            onValueChange={(value) => setLeaderboardData({ ...leaderboardData, event_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an event (optional)" />
            </SelectTrigger>
            <SelectContent>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id}>
                  {event.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          Step 2: Sections
          <Button onClick={addSection} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {sections.map((section, index) => (
          <div key={index} className="border rounded-lg p-4 space-y-4">
            <div className="flex justify-between items-center">
              <Input
                value={section.name}
                onChange={(e) => updateSection(index, 'name', e.target.value)}
                placeholder="Section name (e.g., 11u, 12u)"
                className="max-w-sm"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => moveSection(index, 'up')}
                  disabled={index === 0}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => moveSection(index, 'down')}
                  disabled={index === sections.length - 1}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => removeSection(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Sort by Field</Label>
                <Select
                  value={section.sort_field_id || ''}
                  onValueChange={(value) => updateSection(index, 'sort_field_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select field to sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    {section.fields.map((field) => (
                      <SelectItem key={field.id || field.field_name} value={field.id || field.field_name}>
                        {field.field_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sort Direction</Label>
                <Select
                  value={section.sort_direction || 'desc'}
                  onValueChange={(value) => updateSection(index, 'sort_direction', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Highest to Lowest</SelectItem>
                    <SelectItem value="asc">Lowest to Highest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No sections added yet. Click "Add Section" to get started.
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Step 3: Fields</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold">{section.name || `Section ${sectionIndex + 1}`}</h4>
              <Button onClick={() => addField(sectionIndex)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Field
              </Button>
            </div>
            
            <div className="space-y-3">
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-4">
                    <Label>Field Name</Label>
                    <Input
                      value={field.field_name}
                      onChange={(e) => updateField(sectionIndex, fieldIndex, 'field_name', e.target.value)}
                      placeholder="e.g., 40 Yard Dash"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label>Type</Label>
                    <Select
                      value={field.field_type}
                      onValueChange={(value) => updateField(sectionIndex, fieldIndex, 'field_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label>Unit</Label>
                    <Input
                      value={field.unit}
                      onChange={(e) => updateField(sectionIndex, fieldIndex, 'unit', e.target.value)}
                      placeholder="e.g., seconds"
                    />
                  </div>
                  <div className="col-span-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => removeField(sectionIndex, fieldIndex)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {section.fields.length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                  No fields added yet. Click "Add Field" to get started.
                </div>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const renderStep4 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Step 4: Preview & Save</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="font-semibold text-lg">{leaderboardData.name}</h3>
          {leaderboardData.description && (
            <p className="text-muted-foreground">{leaderboardData.description}</p>
          )}
        </div>
        
        {sections.map((section, index) => (
          <div key={index} className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold">{section.name}</h4>
              {section.sort_field_id && (
                <Badge variant="outline">
                  Sorted by: {section.fields.find(f => f.id === section.sort_field_id || f.field_name === section.sort_field_id)?.field_name} 
                  ({section.sort_direction === 'desc' ? 'High to Low' : 'Low to High'})
                </Badge>
              )}
            </div>
            <div className="grid gap-2">
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="flex justify-between">
                  <span className={(field.id === section.sort_field_id || field.field_name === section.sort_field_id) ? 'font-semibold text-primary' : ''}>
                    {field.field_name}
                    {(field.id === section.sort_field_id || field.field_name === section.sort_field_id) && ' ⭐'}
                  </span>
                  <Badge variant="outline">
                    {field.field_type} {field.unit && `(${field.unit})`}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        ))}
        
        <Button
          onClick={saveLeaderboard}
          disabled={isLoading || !leaderboardData.name || sections.length === 0}
          className="w-full"
        >
          {isLoading ? 'Saving...' : editId ? 'Update Leaderboard' : 'Create Leaderboard'}
        </Button>
      </CardContent>
    </Card>
  );

  if (isLoading && editId) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/admin-dashboard/leaderboards')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leaderboards
        </Button>
        <h1 className="text-3xl font-bold text-primary">
          {editId ? 'Edit Leaderboard' : 'Create Leaderboard'}
        </h1>
      </div>

      <div className="flex justify-center mb-6">
        <div className="flex items-center space-x-4">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep >= step
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {step}
              </div>
              {step < 4 && (
                <div
                  className={`w-16 h-1 ${
                    currentStep > step ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderStep4()}

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(currentStep - 1)}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        <Button
          onClick={() => setCurrentStep(currentStep + 1)}
          disabled={currentStep === 4 || (currentStep === 1 && !leaderboardData.name)}
        >
          Next
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default LeaderboardBuilder;